import React, { useState } from "react";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "./dialog";

interface Bank {
  _id: string;
  name: string;
  branchCode: string;
  universalBranchCode: string;
  country: string;
  iconUrl: string;
  swiftCode: string;
  status: boolean;
  guides: {
    recurringTransfer: {
      title: string;
      steps: string[];
      footNote: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface BankSelectionModalProps {
  banks: Bank[];
  isOpen: boolean;
  onClose: () => void;
  description?: string;
  title?: string;
}

export default function BankSelectionModal({
  banks,
  isOpen,
  onClose,
  title = "Set Up Your AutoStack Recurring Payment",
  description = "Select your bank below for a quick step-by-step guide to schedule your AutoStack deposit.",
}: BankSelectionModalProps) {
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [view, setView] = useState<"list" | "details">("list");

  const handleBankSelect = (bank: Bank) => {
    setSelectedBank(bank);
    setView("details");
  };

  const handleBackToList = () => {
    setSelectedBank(null);
    setView("list");
  };

  const handleClose = () => {
    setSelectedBank(null);
    setView("list");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl p-0 bg-white rounded-xl border-0 shadow-2xl">
        <div className="relative p-8">
          <div className="flex items-center justify-between mb-4">
            {view === "details" && (
              <button
                onClick={handleBackToList}
                className="border border-gray-300 rounded-full p-3 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
                <span className="sr-only">Back to bank list</span>
              </button>
            )}
            {view === "list" && <div />}
            <DialogClose className="border border-gray-300 rounded-full p-3 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-5 w-5 text-gray-600" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          {view === "list" ? (
            <div className="space-y-6 pt-2">
              <div className="text-center space-y-2">
                <DialogTitle className="text-2xl font-semibold text-[#33125F] leading-tight">
                  {title}
                </DialogTitle>

                <DialogDescription className="text-sm text-text-primary leading-relaxed px-8">
                  {description}
                </DialogDescription>
              </div>

              <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="space-y-3 pr-2">
                  {banks.map((bank) => (
                    <button
                      key={bank._id}
                      onClick={() => handleBankSelect(bank)}
                      className="w-full flex items-center justify-between p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 overflow-hidden">
                          <img
                            src={bank.iconUrl}
                            alt={`${bank.name} logo`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="font-medium text-gray-900">
                          {bank.name}
                        </span>
                      </div>
                      <div className="flex items-center justify-center p-2 rounded-full border border-bg-section">
                        <ArrowRight className="h-4 w-4 text-text-secondary group-hover:text-gray-600 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            selectedBank && (
              <div className="space-y-6 px-8">
                <div className="flex items-center justify-center space-x-4">
                  <img
                    src={selectedBank.iconUrl}
                    alt={`${selectedBank.name} logo`}
                    className="w-7 h-7 object-contain"
                  />
                  <DialogTitle className="font-semibold text-text-primary">
                    {selectedBank.name}
                  </DialogTitle>
                </div>

                <div className="text-center">
                  <h3 className="text-2xl font-semibold text-[#33125F] mb-6">
                    {selectedBank.guides.recurringTransfer.title}
                  </h3>
                </div>

                <div className="overflow-y-auto">
                  <div className="space-y-6">
                    <ol className="list-decimal list-outside space-y-1 pl-6">
                      {selectedBank.guides.recurringTransfer.steps.map(
                        (step, index) => (
                          <li
                            key={index}
                            className="text-sm leading-relaxed pl-2"
                          >
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => <span>{children}</span>,
                                strong: ({ children }) => (
                                  <strong className="text-text-primary">
                                    {children}
                                  </strong>
                                ),
                              }}
                            >
                              {step}
                            </ReactMarkdown>
                          </li>
                        )
                      )}
                    </ol>

                    <p className="text-sm leading-relaxed text-text-primary font-semibold">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <span>{children}</span>,
                          strong: ({ children }) => (
                            <strong className="text-text-primary">
                              {children}
                            </strong>
                          ),
                        }}
                      >
                        {selectedBank.guides.recurringTransfer.footNote}
                      </ReactMarkdown>
                    </p>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export type { Bank };
